import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Camera, Check, Trash2, Plus, X, RotateCcw, Lock, UploadCloud, Images } from 'lucide-react';
import { ajouterPhoto, listerEnAttente, retirer, surChangement, lancerEnvoi } from '../utils/fileAttentePhotos';
import { compresser } from '../utils/compressionPhoto';
import { quandLibre } from '../utils/pauseGlobale';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Cache des images deja chargees : evite de retelecharger a chaque rafraichissement
const CACHE = new Map();

const chargerImage = async (cle, url) => {
  if (CACHE.has(cle)) return CACHE.get(cle);
  const promesse = axios.get(url, { responseType: 'blob' })
    .then((r) => URL.createObjectURL(r.data))
    .catch((e) => { CACHE.delete(cle); throw e; });
  CACHE.set(cle, promesse);
  return promesse;
};

const formaterHeure = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const formaterDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} - ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Vignette rapide (charge la miniature de ~25 Ko)
const Vignette = ({ chantierId, photoId, onClick }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let vivant = true;
    chargerImage(`min:${photoId}`, `${API}/api/photos/${chantierId}/${photoId}?min=1`)
      .then((url) => { if (vivant) setSrc(url); })
      .catch(() => {});

    // La version complete est preparee en arriere-plan : au clic, elle est deja la
    const t = setTimeout(() => {
      chargerImage(`full:${photoId}`, `${API}/api/photos/${chantierId}/${photoId}`).catch(() => {});
    }, 400);

    return () => { vivant = false; clearTimeout(t); };
  }, [chantierId, photoId]);

  const style = { width: '76px', height: '76px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #D1FAE5', display: 'block' };

  if (!src) return <div style={{ ...style, background: '#F3F4F6' }} />;
  return <img src={src} alt="" style={{ ...style, cursor: 'pointer' }} onClick={onClick} />;
};

// Vue plein ecran (charge la photo complete)
const PleinEcran = ({ chantierId, photo, onFermer }) => {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let vivant = true;
    // Photo encore en attente d'envoi : on l'affiche depuis le fichier local
    if (photo.local && photo.fichier) {
      const url = URL.createObjectURL(photo.fichier);
      setSrc(url);
      return () => URL.revokeObjectURL(url);
    }
    chargerImage(`full:${photo.id}`, `${API}/api/photos/${chantierId}/${photo.id}`)
      .then((url) => { if (vivant) setSrc(url); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [chantierId, photo.id, photo.local]);

  return (
    <div onClick={onFermer}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 100, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', color: '#fff' }}>
        <div style={{ fontSize: '13px', opacity: 0.85 }}>
          {formaterDate(photo.date)}{photo.par ? ` · ${photo.par}` : ''}
          {photo.local && <span style={{ marginLeft: '8px', color: '#FCD34D' }}>· envoi en cours</span>}
        </div>
        <X size={26} color="#fff" style={{ cursor: 'pointer' }} onClick={onFermer} />
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px' }}>
        {src
          ? <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          : <span style={{ color: '#9CA3AF', fontSize: '14px' }}>Chargement…</span>}
      </div>
    </div>
  );
};

const VignetteLocale = ({ fichier, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    const url = URL.createObjectURL(fichier);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [fichier]);
  const style = { width: '76px', height: '76px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #FCD34D', display: 'block', cursor: 'pointer' };
  if (!src) return <div style={{ ...style, background: '#F3F4F6' }} />;
  return <img src={src} alt="" style={style} onClick={onClick} />;
};

const TechPhotos = ({ chantierId, photos, types, onChange, verrouille, nbMicros }) => {
  const [envoi, setEnvoi] = useState(null);
  const [erreur, setErreur] = useState('');
  const [grande, setGrande] = useState(null);
  const [aRefaire, setARefaire] = useState(null);
  const [aConfirmer, setAConfirmer] = useState(null);
  const [masquees, setMasquees] = useState([]);
  const [reste, setReste] = useState(10);
  const [anime, setAnime] = useState(false);
  const [enAttente, setEnAttente] = useState([]);
  const refs = useRef({});
  const refsGalerie = useRef({});

  const rechargerAttente = async () => {
    const liste = await listerEnAttente(chantierId);
    setEnAttente(liste);
    window.__photosEnAttente = liste.length;
    onChange();
  };

  useEffect(() => {
    if (!aConfirmer) { setAnime(false); return; }
    setReste(10);
    setAnime(false);
    const depart = setTimeout(() => setAnime(true), 30);
    const tic = setInterval(() => {
      setReste((r) => {
        if (r <= 1) { clearInterval(tic); setAConfirmer(null); return 10; }
        return r - 1;
      });
    }, 1000);
    return () => { clearInterval(tic); clearTimeout(depart); };
  }, [aConfirmer]);

  useEffect(() => {
    rechargerAttente();
    const off = surChangement(() => quandLibre(rechargerAttente));
    return () => off();
  }, [chantierId]);

  // Nombre de photos attendues pour les micro-onduleurs
  const attenduPour = (cle) => (cle === 'micro_onduleur' ? (nbMicros || 0) : 1);

  const photosDuType = (cle) => {
    const envoyees = (photos || []).filter((p) => p.type === cle && !masquees.includes(p.id));
    const attente = enAttente
      .filter((p) => p.typePhoto === cle)
      .map((p) => ({ id: p.id, type: cle, date: p.date, fichier: p.fichier, local: true }));
    return [...envoyees, ...attente];
  };

  const envoyer = async (cle, fichierBrut) => {
    if (!fichierBrut) return;
    setErreur('');
    const fichier = await compresser(fichierBrut);
    try {
      // La photo est mise en file : affichage immediat, envoi en arriere-plan
      await ajouterPhoto(chantierId, cle, fichier);
      if (aRefaire) {
        if (String(aRefaire).startsWith('local_')) {
          await retirer(aRefaire);
        } else {
          await axios.delete(`${API}/api/photos/${chantierId}/${aRefaire}`).catch(() => {});
        }
        setARefaire(null);
      }
      await rechargerAttente();
    } catch (e) {
      setErreur("Impossible d'enregistrer la photo");
      setARefaire(null);
    }
  };

  // Une photo reste masquee tant que le serveur la renvoie encore
  useEffect(() => {
    setMasquees((m) => m.filter((id) => (photos || []).some((p) => p.id === id)));
  }, [photos]);

  const supprimer = async (photoId) => {
    setErreur('');
    if (String(photoId).startsWith('local_')) {
      await retirer(photoId);
      return;
    }
    try {
      await axios.delete(`${API}/api/photos/${chantierId}/${photoId}`);
      CACHE.delete(`min:${photoId}`);
      CACHE.delete(`full:${photoId}`);
      onChange();
    } catch (e) {
      setErreur(e.response?.data?.detail || 'Échec de la suppression');
    }
  };

  const refaire = (cle, photoId) => {
    setARefaire(photoId);
    refs.current[cle]?.click();
  };

  const btn = { display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', border: '1px solid #D1D5DB', background: '#fff' };

  return (
    <div>
      {erreur && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>{erreur}</div>
      )}

      {enAttente.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', background: '#FEF3C7', color: '#92400E', padding: '10px 12px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <UploadCloud size={15} />
            {enAttente.length} photo{enAttente.length > 1 ? 's' : ''} en attente d'envoi
          </span>
        </div>
      )}

      {verrouille && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#F3F4F6', color: '#4B5563', padding: '10px', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' }}>
          <Lock size={14} /> Étape validée : les photos ne sont plus modifiables.
        </div>
      )}

      {(types || []).map((t, idx) => {
        const prises = photosDuType(t.cle);
        const attendu = attenduPour(t.cle);
        const fait = attendu > 1 ? prises.length >= attendu : prises.length > 0;

        // La passerelle attend que les micro-onduleurs soient complets
        const precedent = idx > 0 ? (types[idx - 1] || {}).cle : null;
        const bloque = precedent === 'micro_onduleur'
          && photosDuType('micro_onduleur').length < attenduPour('micro_onduleur');

        if (bloque) {
          return (
            <div key={t.cle} style={{ marginBottom: '14px', padding: '14px', border: '1px solid #E5E7EB', background: '#F9FAFB', borderRadius: '10px', opacity: 0.55 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Lock size={18} color="#9CA3AF" style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '15px', color: '#6B7280' }}>{t.label}</div>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>
                    Disponible une fois les micro-onduleurs photographiés
                  </div>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div key={t.cle} style={{ marginBottom: '14px', padding: '14px', border: `1px solid ${fait ? '#A7F3D0' : '#E5E7EB'}`, background: fait ? '#F0FDF4' : '#fff', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: prises.length ? '12px' : 0, gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                {fait
                  ? <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Check size={14} color="#fff" /></span>
                  : <Camera size={20} color="#9CA3AF" style={{ flexShrink: 0 }} />}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '15px', fontWeight: fait ? 500 : 400 }}>
                    {t.label}{attendu > 1 ? ` ${prises.length} / ${attendu}` : (t.multiple && prises.length > 0 ? ` (${prises.length})` : '')}
                  </div>
                  {t.aide && (
                    <div style={{ fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', marginTop: '2px' }}>
                      {t.aide}
                    </div>
                  )}
                </div>
              </div>

              {!verrouille && (attendu > 1 ? prises.length < attendu : (!fait || t.multiple)) && (
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={() => { setARefaire(null); refs.current[t.cle]?.click(); }} disabled={envoi === t.cle}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: 'none', borderRadius: '8px', background: '#10B981', color: '#fff', fontSize: '14px', cursor: 'pointer', opacity: envoi === t.cle ? 0.6 : 1, flexShrink: 0 }}>
                  {envoi === t.cle ? 'Envoi…' : <><Camera size={15} /> Photo</>}
                </button>
                <button onClick={() => { setARefaire(null); refsGalerie.current[t.cle]?.click(); }} disabled={envoi === t.cle}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', background: '#fff', color: '#6B7280', fontSize: '14px', cursor: 'pointer', opacity: envoi === t.cle ? 0.6 : 1, flexShrink: 0 }}>
                  <Images size={15} />
                </button>
                </div>
              )}
            </div>

            <input ref={(el) => (refsGalerie.current[t.cle] = el)} type="file" accept="image/*" multiple
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files; if (f && f.length) { Array.from(f).forEach((x) => envoyer(t.cle, x)); } e.target.value = ''; }} />
            <input ref={(el) => (refs.current[t.cle] = el)} type="file" accept="image/*" capture="environment"
              style={{ display: 'none' }}
              onChange={(e) => { envoyer(t.cle, e.target.files[0]); e.target.value = ''; }} />

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: prises.length ? '6px' : 0 }}>
              {prises.map((p) => (
                <div key={p.id} style={{ position: 'relative', paddingTop: '8px', paddingRight: '8px' }}>
                  {p.local
                    ? <VignetteLocale fichier={p.fichier} onClick={() => setGrande(p)} />
                    : <Vignette chantierId={chantierId} photoId={p.id} onClick={() => setGrande(p)} />}

                  {!verrouille && (
                    <button onClick={(ev) => { ev.stopPropagation(); setAConfirmer(p.id); }}
                      style={{ position: 'absolute', top: 0, right: 0, width: '34px', height: '34px', borderRadius: '50%', border: '2px solid #fff', background: '#EF4444', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.35)' }}>
                      <Trash2 size={16} />
                    </button>
                  )}

                  <div style={{ fontSize: '10px', color: '#6B7280', textAlign: 'center', marginTop: '5px', lineHeight: 1.35, maxWidth: '84px' }}>
                    {p.par && <div style={{ color: '#374151', fontWeight: 500 }}>{p.par}</div>}
                    <div>{formaterDate(p.date)}</div>
                  </div>

                  {p.local && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', background: '#FEF3C7', color: '#92400E', padding: '3px 6px', borderRadius: '6px', fontSize: '10px', marginTop: '4px' }}>
                      <UploadCloud size={10} /> envoi en cours
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {aConfirmer && (
        <div onClick={() => setAConfirmer(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(ev) => ev.stopPropagation()}
            style={{ background: '#fff', borderRadius: '14px', padding: '22px', width: '100%', maxWidth: '340px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '6px' }}>Supprimer cette photo ?</div>
            <div style={{ fontSize: '14px', color: '#6B7280', marginBottom: '16px' }}>Cette action est définitive.</div>

            <div style={{ height: '5px', background: '#F3F4F6', borderRadius: '999px', overflow: 'hidden', marginBottom: '6px' }}>
              <div style={{ height: '100%', width: `${(reste / 10) * 100}%`, background: '#F59E0B', transition: anime ? 'width 1s linear' : 'none' }} />
            </div>
            <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '18px', textAlign: 'right' }}>
              Annulation automatique dans {reste} s
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setAConfirmer(null)}
                style={{ flex: 1, padding: '14px', border: '1px solid #D1D5DB', background: '#fff', borderRadius: '10px', fontSize: '16px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={() => {
                if (navigator.vibrate) navigator.vibrate(30);
                setMasquees((m) => [...m, aConfirmer]);
                supprimer(aConfirmer);
                setAConfirmer(null);
              }}
                style={{ flex: 1, padding: '14px', border: 'none', background: '#DC2626', color: '#fff', borderRadius: '10px', fontSize: '16px', fontWeight: 500, cursor: 'pointer' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {grande && <PleinEcran chantierId={chantierId} photo={grande} onFermer={() => setGrande(null)} />}
    </div>
  );
};

export default TechPhotos;
